import { useEffect, useRef } from 'react';

// §7.1 scroll reveal: opacity 0→1, translateY 14px→0, 450ms ease-out, once:true, threshold 0.15.
// Optional 60ms stagger applied to first N direct children (spec: max 6 staggered items).
export default function useReveal({ stagger = 0, maxStaggered = 6 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible');
      return;
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      el.classList.add('is-visible');
      return;
    }

    if (stagger > 0) {
      const children = Array.from(el.children);
      children.forEach((child, i) => {
        child.classList.add('reveal');
        if (i < maxStaggered) child.style.transitionDelay = `${i * stagger}ms`;
      });
    } else {
      el.classList.add('reveal');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (stagger > 0) {
            Array.from(el.children).forEach((child) => child.classList.add('is-visible'));
          } else {
            el.classList.add('is-visible');
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [stagger, maxStaggered]);

  return ref;
}
